import { useState, RefObject, useRef, useCallback, memo, useEffect } from 'react';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/react';
import { Icon } from '@components/shared/Icon';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';

import { CardStatus } from '@components/learning/LearningSidebar/constants';
import { HoverInfo, useSidebarContext } from '@components/learning/LearningSidebar/context/LearningSidebarContext';

import { LessonCard } from './LessonCard';
import { LessonsSkeletonLoader } from './skeletons/LessonsSkeletonLoader';
import { Appearance, TextTooltip } from './TextTooltip';
import { isEntitySelected, mapLearningProgressToCardStatus } from '../utils/utils';
import { CourseItem, ProgressForLessons } from '../types';
import { svgBadge } from '../assets/badge';

export interface CourseCardProps {
  course: CourseItem;
  status: CardStatus;
  isLoading: boolean;
  expandedLessonIds?: string[];
  expandedCourseIds?: string[];
  progressForLessons?: ProgressForLessons;
  selectedSectionId?: string;
  currentLessonSlug?: string;
  onHover: (data: HoverInfo | null) => void;
  onSelect: (courseSlug: string, lessonSlug?: string, sectionSlug?: string) => void;
}

interface CourseCardPropsWithRef extends CourseCardProps {
  selectedLessonRef?: RefObject<HTMLDivElement>;
}

export const CourseCard: React.FC<Omit<CourseCardPropsWithRef, 'onHover'>> = (props) => {
  const { setHoverInfo } = useSidebarContext();

  return <CourseCardComponent {...props} onHover={setHoverInfo} />;
};

export const CourseCardComponent: React.FC<CourseCardPropsWithRef> = memo(
  ({
    course: { sys, title, reward, slug, lessonsCollection },
    status,
    isLoading,
    selectedLessonRef,
    progressForLessons,
    onHover,
    onSelect,
    expandedLessonIds = [],
    expandedCourseIds = [],
    selectedSectionId,
    currentLessonSlug,
  }) => {
    const courseCardRef = useRef<HTMLDivElement>(null);
    const isSelected = isEntitySelected(slug);

    const [isCourseExpanded, setIsCourseExpanded] = useState(false);

    useEffect(() => {
      const isExpanded = expandedCourseIds.includes(sys.id);

      setIsCourseExpanded(isExpanded || false);
    }, [expandedCourseIds]);

    const courseCardTitle = {
      [CardStatus.COMPLETED]: 'Completed',
      [CardStatus.DEFAULT]: 'Course',
    };

    const numberOfCompletedLessons = lessonsCollection.items.filter(
      ({ sys: { id } }) =>
        mapLearningProgressToCardStatus(
          progressForLessons?.lessons.find((lesson) => lesson.lessonId === id)?.status,
        ) === CardStatus.COMPLETED,
    ).length;
    const numberOfTotalLessons = lessonsCollection.items.length;
    const progress = (numberOfCompletedLessons / numberOfTotalLessons) * 100;

    const isStatusCompleted = progress === 100;

    const handleCourseCardExpand = useCallback(() => {
      setIsCourseExpanded(!isCourseExpanded);
      onSelect(slug);
    }, [isCourseExpanded, onSelect, slug]);

    const showCoursePopup = useCallback(() => {
      if (courseCardRef.current) {
        onHover({ courseId: sys.id, courseCardPosition: courseCardRef.current.getBoundingClientRect() });
      }
    }, [sys.id, onHover]);

    const hideCoursePopup = useCallback(() => {
      onHover(null);
    }, [onHover]);

    return (
      <div
        data-testid="sidebar-course-card"
        css={[courseCardStyles, isCourseExpanded && courseCardOpenedStyles]}
        ref={courseCardRef}
        onMouseEnter={showCoursePopup}
        onMouseLeave={hideCoursePopup}
      >
        <div
          css={[courseCardHeaderStyles, isCourseExpanded && courseCardHeaderOpenedStyles]}
          onClick={handleCourseCardExpand}
          onMouseEnter={showCoursePopup}
          data-testid="course-card-header"
        >
          <div css={[courseExpanderStyles, isCourseExpanded && courseExpanderSelectedStyles]}>
            <Icon
              type={isCourseExpanded ? 'chevron-down' : 'chevron-right'}
              color={isCourseExpanded ? token('color.icon.selected') : token('color.icon')}
            />
          </div>
          <div css={courseTitleContainerStyles}>
            <p
              css={[
                courseCardTitleStyles,
                status === CardStatus.COMPLETED && courseCardTitleCompletedStyles,
                status === CardStatus.DEFAULT && courseCardTitleDefaultStyles,
                isSelected && courseCardTitleSelectedStyles,
              ]}
            >
              {isSelected ? 'Current course'.toUpperCase() : courseCardTitle[status].toUpperCase()}
            </p>
            <TextTooltip text={title} isSelected={isSelected} appearance={Appearance.COURSE} />
          </div>
          <div css={progressCircleContainerStyles} data-testid="course-progress-circle">
            <CircularProgressbarWithChildren
              strokeWidth={8}
              value={progress}
              styles={buildStyles({
                pathColor: token('color.border.success'),
                trailColor: 'rgba(9, 30, 66, 0.14)',
              })}
              css={[isStatusCompleted && completedCourseProgressStyles]}
            >
              <div css={[imageBadgeContainerStyles, isStatusCompleted && completedCourseProgressStyles]}>
                <img
                  css={imageStyles}
                  data-testid="course-badge-image"
                  src={reward?.badgeCdnUrl || reward?.badge?.[0]?.cdn_url || svgBadge}
                  alt={title}
                />
              </div>
            </CircularProgressbarWithChildren>
            {isStatusCompleted && <Icon type="check" color={token('color.icon.success')} />}
          </div>
        </div>
        <div css={lessonContainerStyles} onMouseEnter={hideCoursePopup}>
          {isLoading && isCourseExpanded ? (
            <LessonsSkeletonLoader />
          ) : (
            isCourseExpanded &&
            lessonsCollection.items.map((lesson) => (
              <div key={lesson.sys.id} ref={isEntitySelected('', lesson.slug) ? selectedLessonRef : null}>
                <LessonCard
                  lesson={lesson}
                  courseSlug={slug}
                  progressForLesson={progressForLessons?.lessons.find(
                    (lessonProgress) => lessonProgress.lessonId === lesson.sys.id,
                  )}
                  onSelect={onSelect}
                  isLoading={isLoading}
                  expandedLessonIds={expandedLessonIds}
                  selectedSectionId={selectedSectionId}
                  currentLessonSlug={currentLessonSlug}
                />
              </div>
            ))
          )}
        </div>
      </div>
    );
  },
);

const courseCardStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  paddingTop: token('space.300'),
  paddingBottom: token('space.300'),
  borderBottom: `1px solid ${token('color.border')}`,

  '&:hover': {
    background: '#F7F8F9',
  },
});

const courseCardOpenedStyles = css({
  paddingBottom: 0,

  '&:hover': {
    background: 'none',
  },
});

const courseCardHeaderStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  paddingLeft: token('space.100'),
  paddingRight: token('space.100'),
});

const courseCardHeaderOpenedStyles = css({
  marginBottom: token('space.100'),
});

const courseExpanderStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  border: `1px solid ${token('color.background.accent.gray.subtlest')}`,
  cursor: 'pointer',
});

const courseExpanderSelectedStyles = css({
  border: `1px solid ${token('color.background.accent.blue.subtler')}`,
});

const courseTitleContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: token('space.100'),
  marginRight: 'auto',
});

const courseCardTitleStyles = css({
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
});

const courseCardTitleSelectedStyles = css({
  color: token('color.text.selected'),
});

const courseCardTitleCompletedStyles = css({
  color: token('color.text.success'),
});

const courseCardTitleDefaultStyles = css({
  color: token('color.text.subtlest'),
});

const completedCourseProgressStyles = css({
  opacity: '0.2',
});

const progressCircleContainerStyles = css({
  position: 'relative',
  maxWidth: '32px',

  span: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
});

const imageBadgeContainerStyles = css({
  width: '100%',
  height: '100%',
  padding: token('space.100'),
});

const imageStyles = css({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  borderRadius: '50%',
});

const lessonContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
});
